import { useSelector } from 'react-redux';
import useRealtimeReservations from '../../hooks/useRealtimeReservations';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';
import { useTranslation } from 'react-i18next';
import ReservationItemAgency from '../ReservationItem/ReservationItemAgency';
import ReservationItem from '../ReservationItem/ReservationItem';
import { useCallback, useEffect, useState } from 'react';
import { skeletonLoading } from '../Skeletons/SkeletonAccount';
import { useHistory } from 'react-router-dom';
import AccountFilterIcon from '../icons/AccountFilterIcon';
import { CircleSpinner } from 'react-spinners-kit';
import { getDateFromOffset } from '../../helpers/dates';
import SkeletonReservationList from '../Skeletons/SkeletonReservationList';

const ReservationsList = ({ onRealTimeUpdate }) => {
  const market = useSelector((state) => state.app.currentMarket);
  const { t } = useTranslation();
  const history = useHistory();
  const user = useSelector((state) => state.associates.user);
  const [filterDate, setFilterDate] = useState(
    localStorage.getItem('filterDate') ?? 'RESERVATION_DATE',
  );
  const [selectedOption, setSelectedOption] = useState(filterDate);
  const today = getDateFromOffset(user.companyUtcMinutesOffset);

  const ReservationComponent =
    market?.operationType === 'WELLET_AGENCY' ? ReservationItemAgency : ReservationItem;

  const reservationTypes = {
    ReservationTypeInclude: [],
    ReservationTypeExclude: ['LINK'],
  };

  const handleValidReservationUpdate = useCallback(() => {
    if (onRealTimeUpdate) onRealTimeUpdate();
  }, [onRealTimeUpdate]);

  const handleSelectChange = (selectedOption) => {
    localStorage.setItem('filterDate', selectedOption);
    setFilterDate(selectedOption);
    setSelectedOption(selectedOption);
  };

  const { reservations, loading, hasMore, loadingMore, fetchMoreReservations } =
    useRealtimeReservations(today, filterDate, handleValidReservationUpdate, reservationTypes);

  const isEmptyList = !reservations || reservations.length === 0;

  useEffect(() => {
    const storedFilterDate = localStorage.getItem('filterDate');
    if (storedFilterDate) {
      setFilterDate(storedFilterDate);
    }

    if (!localStorage.getItem('filterDate')) localStorage.setItem('filterDate', 'RESERVATION_DATE');
  }, []);

  return (
    <div>
      <div className='font-normal2 fw-700 text-lighter-gray mt-4'>{t('myReservations')}</div>
      <div className='row mt-3 mb-4'>
        <div className='col-auto pr-0'>
          <div className='account-button' onClick={() => history.push('/all-reservations')}>
            <AccountFilterIcon color='#7E7E7E' size='16px' styles={{ marginTop: '-2px' }} />
          </div>
        </div>
        <div className='col-auto px-1'>
          <div
            className={`account-button ${selectedOption === 'RESERVATION_DATE' ? 'selected' : ''}`}
            onClick={() => handleSelectChange('RESERVATION_DATE')}
          >
            {t('forToday')}
          </div>
        </div>
        <div className='col-auto col-md p-0'>
          <div
            className={`account-button ${selectedOption === 'CREATED_DATE' ? 'selected' : ''}`}
            onClick={() => handleSelectChange('CREATED_DATE')}
          >
            {t('mostRecent')}
          </div>
        </div>
        <div className='col pl-1'>
          <div
            className={`account-button ${
              selectedOption === 'RECENT_COMMISSIONS' ? 'selected' : ''
            }`}
            onClick={() => handleSelectChange('RECENT_COMMISSIONS')}
          >
            {t('lastCommissions')}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-2">
          <SkeletonReservationList />
          <SkeletonReservationList />
          <SkeletonReservationList borderBottom={false} />
        </div>
      ) : (
        <div className='reservations-content'>
          {isEmptyList ? (
            <EmptyActivity subtitle={<div className='bold'>{t('noBookings')}</div>} />
          ) : (
            <>
              {reservations?.map((r, index) => (
                <div key={index}>
                  <ReservationComponent
                    activity={r}
                    date={r?.reservationLocalTime}
                    rightText={r.amount}
                    index={index}
                    lastIndex={reservations.length - 1}
                    handleClick={() => history.push(`/operation-details?id=${r.id}`)}
                  />
                </div>
              ))}
              {loadingMore ? (
                <div className='p-3 d-flex justify-content-center'>
                  <CircleSpinner
                    className='spinner-app mx-auto'
                    style={{ margin: 'auto' }}
                    size={40}
                    color='var(--color-font-primary)'
                  />
                </div>
              ) : (
                hasMore && (
                  <div className='mt-2'>
                    <button className='account-button w-100 fw-600' onClick={fetchMoreReservations}>
                      {t('showMore')}
                    </button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReservationsList;
